import React from 'react'
import { toast } from 'sonner' // You might still use toast elsewhere, but not for enterprise in this version
import { Button } from '../../ui/button' // Assuming this path is correct for your Shadcn UI Button

const PlanCard = ({
  plan,
  currentPlanTier, // This should be the tier of the user's *active* subscription
  currentPlanPrice, // This should be the price (amount) of the user's *active* subscription's price model
  onSelectPlan,
  isYearlyBilling, // This indicates the billing period for the *currently displayed card*
  activeSubscriptionPeriodType, // This indicates the billing period of the user's *active* subscription
}) => {
  // Determine the price details based on the selected billing period for the CARD ITSELF
  const priceDetails = isYearlyBilling ? plan.price.yearly : plan.price.monthly
  const displayAmount = priceDetails ? (priceDetails.amount / 100).toFixed(2) : 'N/A'
  const displayCurrency = priceDetails ? priceDetails.currency : ''

  // Check if this plan card's tier matches the user's current active subscription tier
  const isCurrentTier = currentPlanTier === plan.tier

  // Check if it's the current plan AND the current billing period
  const isCurrentPlanAndPeriod =
    isCurrentTier && activeSubscriptionPeriodType === (isYearlyBilling ? 'yearly' : 'monthly')

  // Determine if the user is attempting to change their billing period for the SAME tier
  const isChangingBillingPeriod =
    isCurrentTier && activeSubscriptionPeriodType !== (isYearlyBilling ? 'yearly' : 'monthly')

  // isDowngradeOption: Check if a plan is explicitly cheaper than the current one
  // This applies when changing tiers OR changing billing periods (e.g., yearly to monthly can be a downgrade in total annual cost)
  const isDowngradeOption = currentPlanPrice !== null && priceDetails.amount < currentPlanPrice

  // Determine the button label
  let buttonLabel = ''
  if (isCurrentPlanAndPeriod) {
    buttonLabel = 'Current Plan'
  } else if (isChangingBillingPeriod) {
    // If we are on the same tier but changing billing periods
    if (isYearlyBilling) {
      // If this card is Yearly, and the active subscription is Monthly (meaning switching to Yearly)
      buttonLabel = 'Upgrade to Yearly' // Or 'Switch to Yearly'
    } else {
      // If this card is Monthly, and the active subscription is Yearly (meaning switching to Monthly)
      buttonLabel = 'Upgrade to Monthly' // Or 'Switch to Monthly'
    }
  } else if (isDowngradeOption) {
    // For actual tier downgrades (assuming 'isChangingBillingPeriod' handles same-tier period changes)
    buttonLabel = 'Upgrade'
  } else {
    // For actual tier upgrades or new purchases
    buttonLabel = 'Upgrade' // Or 'Select Plan' if not logged in / no active sub
  }

  return (
    <div
      className={`bg-white rounded-xl shadow-lg p-6 border-2 ${
        isCurrentPlanAndPeriod ? 'border-heading-primary' : 'border-gray-200'
      } flex flex-col justify-between transition-all duration-300 hover:shadow-xl`}
    >
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
        <p className="text-3xl font-extrabold text-heading-primary mb-4">
          {/* Display actual price for enterprise if available, otherwise 'N/A' or 'Custom' */}
          {priceDetails && displayAmount !== 'N/A'
            ? `${displayCurrency}${displayAmount}`
            : 'Custom'}
          {displayAmount !== 'N/A' && ( // Only show /year or /month if a price is displayed
            <span className="text-base text-gray-500 font-normal">
              /{isYearlyBilling ? 'year' : 'month'}
            </span>
          )}
        </p>
        <div
          className="text-gray-700 text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: plan.description }}
        />
      </div>
      <div className="mt-6">
        {isCurrentPlanAndPeriod ? (
          <Button
            className="w-full bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-md cursor-not-allowed shadow-sm"
            disabled
          >
            {buttonLabel} {/* Use the calculated buttonLabel */}
          </Button>
        ) : (
          // Default button for selecting all other plans (upgrade/downgrade/new purchase), including enterprise
          <Button
            onClick={() => onSelectPlan(plan)}
            className="w-full bg-heading-primary hover:bg-[#643d73] text-white font-semibold py-2 px-4 rounded-md shadow-sm"
          >
            {buttonLabel} {/* Use the calculated buttonLabel */}
          </Button>
        )}
      </div>
    </div>
  )
}

export default PlanCard
